/**
 * Active-stream store — the tiny piece of state the composer reads to
 * know "is a stream currently open, and if so, which one."
 *
 * Module-level Zustand atom (no persistence). The active stream is a
 * session-scoped concept; on page refresh it clears, which is the
 * right semantics — a stream is the explicit act of "I'm opening one
 * now and closing it later," not a durable identity.
 *
 * Only the Capture composer and the ActiveStreamBanner read from this.
 * Everything else (the feed query, the cells, the stream-note viewer)
 * reads from the durable capture_event ledger + the note row.
 */
"use client";

import { create } from "zustand";

interface ActiveStreamState {
  /** The stream parent note's id, or null when no stream is open. */
  streamId: string | null;
  /** Best-effort title (may be null while untitled). */
  title: string | null;
  /** UTC ISO when the active stream opened. */
  startedAt: string | null;
  /** Bullet count denormalized from the parent note. Optimistic. */
  bulletCount: number;

  activate: (input: { streamId: string; title?: string | null; startedAt?: string | null }) => void;
  incrementBullets: () => void;
  setTitle: (title: string | null) => void;
  clear: () => void;
}

export const useActiveStreamStore = create<ActiveStreamState>((set) => ({
  streamId: null,
  title: null,
  startedAt: null,
  bulletCount: 0,

  activate: ({ streamId, title, startedAt }) =>
    set({
      streamId,
      title: title ?? null,
      startedAt: startedAt ?? new Date().toISOString(),
      bulletCount: 0,
    }),

  incrementBullets: () =>
    set((s) => ({ bulletCount: s.bulletCount + 1 })),

  setTitle: (title) => set({ title }),

  clear: () =>
    set({
      streamId: null,
      title: null,
      startedAt: null,
      bulletCount: 0,
    }),
}));

/** Non-React snapshot — for the composer's submit path. */
export function getActiveStreamId(): string | null {
  return useActiveStreamStore.getState().streamId;
}
